
// src/pages/ContentEditor/ContentEditor.js
import React from 'react';
import { Box, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import EditorBreadcrumbs from './components/EditorBreadcrumbs';
import EditorHeader from './components/EditorHeader';
import BasicInfoCard from './components/BasicInfoCard';
import EditorTabs from './components/EditorTabs';
import ActionButtons from './components/ActionButtons';
import DiscardChangesDialog from './components/DiscardChangesDialog';
import { useContentEditorState } from './hooks/useContentEditorState';
import StandardErrorAlert from '../../../../components/alerts/StandardErrorAlert';
import FullPageCircularProgress from '../../../../components/progressBar/FullPageCircularProgress';

const ContentEditor = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  const {
    content,
    formData,
    isNewContent,
    loading,
    saving,
    error,
    tabValue,
    unsavedChanges,
    discardDialogOpen,
    handleTabChange,
    handleInputChange,
    handleNestedChange,
    handleAddItem,
    handleRemoveItem,
    handleSave,
    handleCancel,
    handleDiscardConfirm,
    handleDiscardCancel,
    recreatingCover,
    handleRecreateCover  } = useContentEditorState();

  // Render loading state
  if (loading) {
    return (
      <FullPageCircularProgress/>
    );
  }

  // Render error state
  if (error && !formData) {
    return (
      <Box sx={{ mt: 3 }}>
       <StandardErrorAlert>
          {error}
        </StandardErrorAlert>
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2, color: theme.palette.text.primary }}
          onClick={() => navigate('/')}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box>
       <EditorBreadcrumbs 
        isNewContent={isNewContent}
        contentId={content.id}
        contentTitle={content.cover.title}
      />
      
    <EditorHeader 
        isNewContent={isNewContent}
        contentTitle={content.cover.title}
        saving={saving}
        unsavedChanges={unsavedChanges}
        onCancel={handleCancel}
        onSave={handleSave}
      />
      
      {error && (
        <StandardErrorAlert>
          {error}
        </StandardErrorAlert>
      )}
      
       <BasicInfoCard 
        formData={formData}
        onInputChange={handleInputChange}
        recreatingCover={recreatingCover}
        onRecreateCover={handleRecreateCover}
      />
      
      <EditorTabs 
        tabValue={tabValue}
        formData={formData}
        onTabChange={handleTabChange}
        onNestedChange={handleNestedChange}
        onAddItem={handleAddItem}
        onRemoveItem={handleRemoveItem}
      />
      
      <ActionButtons 
        isNewContent={isNewContent}
        saving={saving}
        unsavedChanges={unsavedChanges}
        onCancel={handleCancel}
        onSave={handleSave}
      />
      
      <DiscardChangesDialog 
        open={discardDialogOpen}
        onCancel={handleDiscardCancel}
        onConfirm={handleDiscardConfirm}
      /> 
    </Box>
  );
};

export default ContentEditor;
